import React, { useState, useEffect, useRef, useMemo } from 'react';
import Toolbar from '@material-ui/core/Toolbar';
import Card from '@material-ui/core/Card';
import { ITransformer } from '../../../@types/model/transformer/transformer';
import MaterialTable from '../../customComponents/materialTable/Table';
import TransformerDropdown from '../../customComponents/selector/transformer/TransformerSelector';
import TransformerMunicipalityReadingHelper, { ITransformerMunicipalityReading } from '../../../@types/model/transformer/reading/transformerMunicipalityReading';
import generalFunctions from '../../../store/general/functions';
import lodash from 'lodash';
import appFunctionsService from '../../../services/appFunctionsService';
import TransformerMeterReadingProblem from '../../customComponents/TransformerMeterReadingProblem';
import Tooltip from '@material-ui/core/Tooltip';
import { IRootState } from '../../../@types/redux';
import { connect } from 'react-redux';
import { IUserSession } from '../../../@types/employee';
import { firestore } from 'firebase/app';
import { Warning } from '@material-ui/icons';
import moment from 'moment';
import { Fab, Icon, IconButton } from '@material-ui/core';
import { CSVLink } from 'react-csv';
import { CSVLinkProps } from '../../../@types/csv';
import DateRangePicker from '../../customComponents/input/DateRangePicker';
import TransformerMunicipalityReadingInfoDialog from './dialog/invoice/municipality/MunicipalityMeterReadingInfoDialog';
import MunicipalityMeterCreateDialog from './dialog/invoice/municipality/MunicipalityMeterReadingCreate';
import TransformerMunicipalityReadingDeleteDialog from './dialog/invoice/municipality/MunicipalityMeterReadingDeleteDialog';
import { IMeterReadingsView } from '../../../@types/model/transformer/reading/transformerReading';
import TransformerMunicipalityMeterReportDialog from '../dialog/MunicipalityMeterReport';
import MunicipalityApproveDialog from './dialog/invoice/municipality/MunicipalityApproveDialog';

interface ITransformerMunicipalityMeterListProps {
    isTransformerAdmin : boolean;
    isTransformerMunicipalityTester : boolean;

    session ?: IUserSession | null;
}

const TransformerMunicipalityMeterListFunctional = (props : ITransformerMunicipalityMeterListProps) : React.ReactElement => {
    const [transformerNumber, setTransformerNumber] = useState<string | undefined>(undefined);
    const [readings, setReadings] = useState<Array<ITransformerMunicipalityReading>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dateFrom, setDateFrom] = useState<moment.Moment | null>(moment.utc().startOf('month').subtract(1, 'months'));
    const [dateTo, setDateTo] = useState<moment.Moment | null>(moment.utc().startOf('day'));
    const [onlyErrors, setOnlyErrors] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<Array<IMeterReadingsView>>([]);
    const [showApproveDialog, setShowApproveDialog] = useState<boolean>(false);
    const [showDisapproveDialog, setShowDisapproveDialog] = useState<boolean>(false);

    const csvLink = useRef<CSVLinkProps & HTMLAnchorElement>(null);
    const readingListener = useRef<(() => void) | undefined>(undefined);

    useEffect(() => {
        loadData();
        return () => {
            if (readingListener.current) {
                readingListener.current();
            }
        };
    }, [props.session]);

    const loadData = async () : Promise<void> => {
        if (readingListener.current) readingListener.current();

        setIsLoading(true);

        const session = props.session;

        if (!session) return;

        readingListener.current = TransformerMunicipalityReadingHelper.listen().onSnapshot((snap) => {
            setReadings(prevReadings => {
                const currentReadings = prevReadings.slice();
    
                snap.docChanges().forEach((f) => {
                    const item = f.doc.data();
                    if (
                        !session.employee.IsTransformerAdmin &&
                        item.division &&
                        session.employee.Areas.length &&
                        !session.employee.Areas.includes(item.division)) return;
    
                    const index = lodash.findIndex(currentReadings, n => n.id === item.id);
    
                    switch (f.type) {
                        case 'added':
                            currentReadings.push(item);
                            break;
                        case 'modified':
                            currentReadings.splice(index, 1, item);
                            break;
                        case 'removed':
                            currentReadings.splice(index, 1);
                            break;
                    }
                });
                return currentReadings;
            });
            setIsLoading(false);
        }, (err) => {
            generalFunctions.generalShowErrorSnackbar(err.message);
        });
    };

    const hasError = (reading : IMeterReadingsView, other ?: IMeterReadingsView | null) : string => {
        const errors : Array<string> = [];

        if (reading.Usage < 0) {
            errors.push('Transformer reading has a negative usage');
        }

        if (other) {
            errors.push('Reading already captured for selected month');
        }

        return errors.join(', ');
    };

    const getMappedData = useMemo<Array<IMeterReadingsView>>(() => {
        const result : Array<IMeterReadingsView> = lodash.chain(readings)
            .filter(n => !transformerNumber || n.transformerRef === transformerNumber)
            .flatMap(n => n.transformerMeterNumberReadings.map(reading => ({
                ImageFileName: reading.imageFileName,
                ImageFileUrl: reading.imageFileUrl,
                MeterNumber: reading.meterNumber,
                NumberOfDigits: reading.numberOfDigits,
                Reading: reading.reading,
                Usage: reading.usage,
                Guid: n.guid,
                ItemNumber: reading.itemNumber,
                Date: n.date,
                Conditions: n.conditions,
                EPNumber: n.transformerRef,
                CreatedByName: n.createdByName,
                UpdatedByName: n.updatedByName,
                CreatedByEmployee: n.createdByEmployee,
                UpdatedByEmployee: n.updatedByEmployee,
                Division: n.division,
                Group: n.group,
                Rollover: reading.rollover,
                CreatedBy: n.createdBy,
                UpdatedBy: n.updatedBy,
                isWeb: n.isWeb,
                RecordingFileName: '',
                Approved: reading.approved,
                ApprovedBy: reading.approvedBy,
                ApprovedByEmployee: reading.approvedByEmployee,
                ApprovedByName: reading.approvedByName,
                ApprovedOn: reading.approvedOn,
            } as IMeterReadingsView)))
            .filter(n => !dateFrom || n.Date >= dateFrom.valueOf())
            .filter(n => !dateTo || n.Date <= dateTo.valueOf())
            .map((reading, i, all) => ({
                ...reading,
                Error: hasError(reading, lodash.find(all, x =>
                    x.EPNumber === reading.EPNumber &&
                    x.Guid !== reading.Guid &&
                    moment.utc(x.Date).format('MM-YYYY') === moment.utc(reading.Date).format('MM-YYYY')
                )),
            }))
            .sort((a, b) => b.Date - a.Date)
            .value();

        return result;
    }, [readings, transformerNumber, dateFrom, dateTo]);
    
    const getFilteredData = useMemo<Array<IMeterReadingsView>>(() => {
        const result : Array<IMeterReadingsView> = lodash.chain(getMappedData)
            .filter(n => !onlyErrors || !!n.Error)
            .sort((a, b) => b.Date - a.Date)
            .value();

        return result;
    }, [getMappedData, onlyErrors]);

    const onEPNumberChange = (value ?: ITransformer) : void => {
        setTransformerNumber(value?.EPNumber);
    };

    const onSave = (meterReading : IMeterReadingsView) : void => {
        const session = props.session;

        if (!session) return;
        
        const readingIndex = readings.findIndex(x => x.guid === meterReading.Guid);

        if (readingIndex < 0) return;

        const reading = readings[readingIndex];
        const meterIndex = reading.transformerMeterNumberReadings.findIndex(x => x.itemNumber === meterReading.ItemNumber);

        if (meterIndex < 0) return;

        const update = {
            ...reading,
            transformerMeterNumberReadings: [
                ...reading.transformerMeterNumberReadings,
            ],
        };

        update.transformerMeterNumberReadings[meterIndex].reading = meterReading.Reading;
        update.transformerMeterNumberReadings[meterIndex].rollover = meterReading.Rollover ?? false;
        update.transformerMeterNumberReadings[meterIndex].imageFileName = meterReading.ImageFileName;
        update.conditions = [ ...meterReading.Conditions ];
        update.date = meterReading.Date.valueOf();
        update.updatedBy = session.firebaseUser.uid;
        update.updatedByName = session.firebaseUser.displayName ?? '';
        update.updatedByEmployee = session.employee.EmployeeNumber ?? '';
        update.updatedOn = firestore.Timestamp.now().toMillis();

        TransformerMunicipalityReadingHelper.save(update);
    };

    const onDelete = async (meterReading : IMeterReadingsView) : Promise<void> => {
        const session = props.session;

        if (!session?.employee.IsTransformerAdmin) return;

        const reading = lodash
            .chain(readings)
            .filter(x => x.transformerRef === meterReading.EPNumber)
            .sortBy(x => x.date)
            .find(x => x.guid === meterReading.Guid)
            .value();

        await TransformerMunicipalityReadingHelper.delete(reading.id);
    };
    
    const onDateSelectionChange = (start : moment.Moment | null, end : moment.Moment | null) : void => {
        setDateFrom(!start ? null : moment(start));
        setDateTo(!end ? null : moment(end));
    };

    const onShowOnlyErrorClick = () : void => {
        setOnlyErrors(!onlyErrors);
    };

    const onCSVClick = () : void => {
        if (csvLink.current) {
            csvLink.current.link.click();
        }
    };

    const getCSVData = useMemo<Array<any>>(() => {
        return getFilteredData.map(n => ({
            EPNumber: n.EPNumber,
            Date: appFunctionsService.formatDateTime(n.Date),
            Division: n.Division,
            Group: n.Group,
            Meter: n.MeterNumber,
            Reading: n.Reading,
            LastReading: n.Rollover ?
                n.Reading - n.Usage + Number(new Array(n.NumberOfDigits).fill('9').join('')) :
                n.Reading - n.Usage,
            Usage: n.Usage,
            CreatedByName: n.CreatedByName,
            CreatedByEmployee: n.CreatedByEmployee,
            Problems: n.Conditions.join('; '),
            Approved: n.Approved,
            ApprovedOn: !n.ApprovedOn ? '' : appFunctionsService.formatDateTime(n.ApprovedOn),
            ApprovedBy: n.ApprovedBy,
            ApprovedByName: n.ApprovedByName,
            ApprovedByEmployee: n.ApprovedByEmployee,
        }));
    }, [getFilteredData]);

    const onSelectionChanged = (rows : Array<IMeterReadingsView>) : void => {
        setSelectedRows(rows);
    };

    const onApproveSelectedClick = () : void => {
        setShowApproveDialog(true);
    };

    const onApproveClick = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) : void => {
        const readings = getFilteredData;
        const reading = readings.find(x => x.Guid === event.currentTarget.value);

        if (reading) {
            setSelectedRows([reading]);
            setShowApproveDialog(true);
        }
    };

    const onDisapproveSelectedClick = () : void => {
        setShowDisapproveDialog(true);
    };

    const onDisapproveClick = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) : void => {
        const readings = getFilteredData;
        const reading = readings.find(x => x.Guid === event.currentTarget.value);
        
        if (reading) {
            setSelectedRows([reading]);
            setShowDisapproveDialog(true);
        }
    };

    const onApproveClose = (result : boolean) : void => {
        if (result) {
            setSelectedRows([]);
        }
        setShowApproveDialog(false);
    };

    const onDisapproveClose = (result : boolean) : void => {
        if (result) {
            setSelectedRows([]);
        }
        setShowDisapproveDialog(false);
    };

    const { isTransformerAdmin, session, isTransformerMunicipalityTester } = props;
    const readingsData = getFilteredData;

    const csvName = moment().valueOf();
    const csvData = getCSVData;

    return (
        <div className={'fdc flx1 p10 mh0 mw0 bcg0'}>
            <Toolbar>
                <span className={'flx1'} />
                <div style={{ paddingRight: '15px' }}>
                    {
                        !!selectedRows.length &&
                        !!selectedRows.some(n => !n.Approved) &&
                        isTransformerMunicipalityTester &&
                        <Tooltip title='Approve Selected'>
                            <Fab color='primary' size='small' onClick={onApproveSelectedClick}>
                                <Icon>
                                    check
                                </Icon>
                            </Fab>
                        </Tooltip>
                    }
                </div>
                <div style={{ paddingRight: '15px' }}>
                    {
                        !!selectedRows.length &&
                        !!selectedRows.some(n => n.Approved) &&
                        isTransformerMunicipalityTester &&
                        <Tooltip title='Disapprove Selected'>
                            <Fab color='primary' size='small' onClick={onDisapproveSelectedClick}>
                                <Icon>
                                    close
                                </Icon>
                            </Fab>
                        </Tooltip>
                    }
                </div>
                <div style={{ paddingRight: '15px' }}>
                    <TransformerMunicipalityMeterReportDialog fullScreen readings={readingsData} />
                </div>
                <div className='pr20'>
                    <Tooltip title='Show only Errors'>
                        <Fab color={onlyErrors ? 'primary' : 'default'} size='small' onClick={onShowOnlyErrorClick}>
                            <Icon className='fs20'>
                                warning
                            </Icon>
                        </Fab>
                    </Tooltip>
                </div>
                <div className={'aic p5 mb10 w350'}>
                    <DateRangePicker onChange={onDateSelectionChange} start={dateFrom} end={dateTo} />
                </div>
                <div className={'fdr pt5 jcfe aic'}>
                    <div className='fdc flx1 w240'>
                        <TransformerDropdown
                            type='municipality'
                            value={transformerNumber}
                            label='Select Transformer'
                            fullWidth
                            onChange={onEPNumberChange}
                        />
                    </div>
                </div>
            </Toolbar>
            <Card className={'flx1 fdc mb70'}>
                <MaterialTable<IMeterReadingsView>
                    id='transformerReadingsTable'
                    data={readingsData}
                    isLoading={isLoading}
                    rowsPerPage={50}
                    externalSort
                    onExportCsvClick={onCSVClick}
                    selectable
                    onSelectionChanged={onSelectionChanged}
                    selectedRows={selectedRows}
                    columns={[{
                        header: '',
                        field: 'EPNumber',
                        renderCell: row => (
                            <div className=' fdr aic'>
                                <TransformerMunicipalityReadingInfoDialog key={`${row.Guid}_${row.ItemNumber}_edit`}
                                    reading={row}
                                    enabled={(isTransformerAdmin || (row.CreatedBy === session?.firebaseUser.uid))}
                                    onSave={onSave}
                                />
                                {
                                    (isTransformerAdmin || (row.CreatedBy === session?.firebaseUser.uid))
                                    && !row.Approved &&
                                    <TransformerMunicipalityReadingDeleteDialog
                                        key={`${row.Guid}_${row.ItemNumber}_delete`}
                                        reading={row}
                                        onDelete={onDelete}
                                    />

                                }
                                {
                                    !!row.isWeb &&
                                    <Icon className='cp ml10'>
                                        laptop
                                    </Icon>                                            
                                }
                                {
                                    !row.isWeb &&
                                    <Icon className='cp ml10'>
                                        smartphone
                                    </Icon>                                            
                                }
                                {
                                    !!row.Error &&
                                    <Tooltip title={row.Error}>
                                        <Warning className={'cy pl10'}/>
                                    </Tooltip>
                                }
                            </div>
                        ),
                    }, {
                        header: 'Date',
                        field: 'Date',
                        width: 175,
                        enableSort: true,
                        enableFilter: true,
                        renderCell: row => (<span>{appFunctionsService.formatDateTimeToDateOnly(row.Date)}</span>),
                    }, {
                        header: 'EP Point',
                        field: 'EPNumber',
                        width: 145,
                        enableSort: true,
                        enableFilter: true,
                    }, {
                        header: 'Area',
                        field: 'Division',
                        width: 145,
                        enableSort: true,
                        enableFilter: true,
                    }, {
                        header: 'Group',
                        field: 'Group',
                        width: 145,
                        enableSort: true,
                        enableFilter: true,
                    }, {
                        header: 'Meter',
                        field: 'MeterNumber',
                        width: 145,
                        enableSort: true,
                        enableFilter: true,
                    }, {
                        header: 'Meter Reading (kWh)',
                        field: 'Reading',
                        width: 200,
                        enableSort: true,
                        enableFilter: true,
                    }, {
                        header: 'Last Reading (kWh)',
                        field: 'Usage',
                        width: 200,
                        enableSort: true,
                        enableFilter: true,
                        renderCell: row => (
                            <span>
                                {
                                    row.Rollover ?
                                        row.Reading - row.Usage + Number(new Array(row.NumberOfDigits).fill('9').join('')) :
                                        row.Reading - row.Usage
                                }
                            </span>),
                    }, {
                        header: 'Usage',
                        field: 'Usage',
                        width: 145,
                        enableSort: true,
                        enableFilter: true,
                    }, {
                        header: 'Created By',
                        field: 'CreatedByName',
                        width: 145,
                        enableSort: true,
                        enableFilter: true,
                        renderCell: row => (
                            <Tooltip title={row.CreatedByEmployee ?? ''}>
                                <div>
                                    {
                                        row.CreatedByName
                                    }
                                </div>
                            </Tooltip>
                        ),
                    }, {
                        header: 'Problems',
                        field: 'Conditions',
                        renderCell: row => (<span className='fdr cw'>{row.Conditions.map((n, i) => (<TransformerMeterReadingProblem key={`${i}_${row.EPNumber}_problem`} text={n} />))}</span>),
                    }, {
                        header: 'APPROVE',
                        width: '175px',
                        renderCell: row => (
                            <div>
                                {
                                    !row.Approved &&
                                    <Tooltip title='Approve'>
                                        <IconButton value={row.Guid} onClick={onApproveClick}>
                                            <Icon className={'cpd'}>
                                                check_circle
                                            </Icon>
                                        </IconButton>
                                    </Tooltip>
                                }
                                {
                                    !!row.Approved && !!row.ApprovedOn &&
                                    <Tooltip title={`${appFunctionsService.formatDateTime(row.ApprovedOn)}`}>
                                        <span>{row.ApprovedByName}</span>
                                    </Tooltip>
                                }
                                {
                                    !!row.Approved && !!row.ApprovedOn &&
                                    <Tooltip title='Disapprove'>
                                        <IconButton value={row.Guid} onClick={onDisapproveClick}>
                                            <Icon className={'cr'}>
                                                close
                                            </Icon>
                                        </IconButton>
                                    </Tooltip>
                                }
                            </div>
                        ),
                    }]}
                />
            </Card>
            <div className='fdr jcfe'>
                <MunicipalityMeterCreateDialog aria-label='add' session={session} />
            </div>
            <CSVLink
                data={csvData}
                headers={[
                    {
                        key: 'EPNumber',
                        label: 'EP Number',
                    },
                    {
                        key: 'Date',
                        label: 'Date',
                    },
                    {
                        key: 'Division',
                        label: 'Area',
                    },
                    {
                        key: 'Group',
                        label: 'Group',
                    },
                    {
                        key: 'Meter',
                        label: 'Meter',
                    },
                    {
                        key: 'Reading',
                        label: 'Reading',
                    },
                    {
                        key: 'LastReading',
                        label: 'Last Reading',
                    },
                    {
                        key: 'Usage',
                        label: 'Usage',
                    },
                    {
                        key: 'CreatedByName',
                        label: 'Created By Name',
                    },
                    {
                        key: 'CreatedByEmployee',
                        label: 'Created By Employee',
                    },
                    {
                        key: 'Problems',
                        label: 'Problems',
                    },
                    {
                        key: 'Approved',
                        label: 'Approved',
                    },
                    {
                        key: 'ApprovedByName',
                        label: 'Approved By Name',
                    },
                    {
                        key: 'ApprovedByEmployee',
                        label: 'Approved By Employee',
                    },
                    {
                        key: 'ApprovedOn',
                        label: 'Approved On',
                    },
                ]}
                filename={`${csvName}_municipality_transformers.csv`}
                className='dn'
                ref={csvLink}
                target='_blank'/>
            {
                session &&
                <MunicipalityApproveDialog
                    open={showApproveDialog}
                    onClose={onApproveClose}
                    values={selectedRows}
                    title='Approve Readings'
                    readings={readings}
                    session={session}
                />
            }
            {
                session &&
                <MunicipalityApproveDialog
                    disapprove
                    open={showDisapproveDialog}
                    onClose={onDisapproveClose}
                    values={selectedRows}
                    title='Disapprove Readings'
                    readings={readings}
                    session={session}
                />
            }
        </div>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        isTransformerAdmin: !!state.auth.session?.employee.IsTransformerAdmin,
        isTransformerMunicipalityTester: !!state.auth.session?.employee.IsTransformerMunicipalityTester,
        session: state.auth.session,
    };
};

const TransformerMunicipalityMeterList = connect(
    mapStateToProps,
)(TransformerMunicipalityMeterListFunctional);

export default TransformerMunicipalityMeterList;
